import React from "react";
import { createPortal } from "react-dom";
import { Icon } from "./gedIcons";
import {
  GED_MASKS, GED_TOKENS, GED_TREE, GedStore, gedCabinet, gedCode, gedDefaults, gedExt, gedFmt, gedISO, gedJurName,
  gedCorrespond, gedEstJuridique, gedMoi, gedNomOrigine, gedNorm, gedParts, gedPathLabel, gedRecipient, gedSubs, gedToday, gedTokens,
} from "./gedModel";
import { GED_EXTENSIONS, GED_TAILLE_MAX } from "./gedModel";
import { chargerGedApi, creerSousDossierApi, deposerApi, modifierDocumentApi, supprimerDocumentApi, televerser } from "./gedApi";
import { showStatusToast } from "../../components/ui/Toast";

// GED du client : arborescence, recherche, filtres, dépôt avec masques de
// nommage, visibilité partagée / interne, notification du client, aperçu.
// Repris de ged.jsx (maquette Claude Design). Adaptations : documents réels lus
// et écrits via l'API (fichiers sur Vercel Blob), aperçu réel des PDF et
// images, mail de notification envoyé par le serveur, noms au format du
// cabinet (« Comptes annuels 2025.pdf »), masque facultatif dans Juridique
// (nom d'origine gardé), utilisateur et cabinet de MyALFA, fenêtre de dépôt
// rendue dans un conteneur qui porte les styles de l'écran.

const GedSeg = ({ value, options, onChange }) => (
  <div className="ged-seg">{options.map(([v, l]) => <button key={v} className={value === v ? "on" : ""} onClick={() => onChange(v)}>{l}</button>)}</div>
);

const GedFolderSelect = ({ value, st, onChange, allowNew }) => (
  <select value={value} onChange={(e) => onChange(e.target.value)}>
    {GED_TREE.map((r) =>
      <optgroup key={r.id} label={r.l}>
        <option value={r.id}>{r.l}</option>
        {gedSubs(r, st).map((s) => <option key={s.id} value={`${r.id}/${s.id}`}>{r.l} › {s.l}</option>)}
        {r.dyn && allowNew && <option value="__newjur">＋ Nouveau sous-dossier juridique…</option>}
      </optgroup>
    )}
  </select>
);

const GedJurFields = ({ v, onChange }) => (
  <div className="ged-jur-new">
    <label className="ged-f"><span>Date *</span><input type="date" value={v.date || ""} onChange={(e) => onChange({ ...v, date: e.target.value })} /></label>
    <label className="ged-f"><span>Intitulé *</span><input value={v.objet || ""} placeholder="ex. Transfert du siège social" onChange={(e) => onChange({ ...v, objet: e.target.value })} /></label>
    <span className="ged-jur-prev"><Icon name="folder" size={13} /> {v.date && (v.objet || "").trim() ? gedJurName(v.date, v.objet) : "AAAA MM JJ - Intitulé"}</span>
  </div>
);

// Ligne de dépôt : masque + champs + dossier + visibilité → nom final
const GedNamePreview = ({ parts, ext }) => (
  <code className="ged-name">{parts.map((p, i) => <span key={i} className={p.missing ? "miss" : ""}>{p.missing ? `{${p.txt}}` : p.txt}</span>)}{ext}</code>
);

const GedFileRow = ({ f, client, st, onChange, onRemove, rename }) => {
  const mask = GED_MASKS.find((m) => m.id === f.mask);
  const set = (patch) => onChange({ ...f, ...patch });
  const libre = gedEstJuridique(f.folder);
  return (
    <div className="ged-frow">
      <div className="ged-frow-h">
        <Icon name="fileText" size={14} />
        <span className="orig" title={f.orig}>{f.orig}</span>
        {onRemove && <button className="x-btn ged-x" onClick={onRemove} title="Retirer"><Icon name="x" size={13} /></button>}
      </div>
      <div className="ged-frow-g">
        <label className="ged-f"><span>Masque de renommage{libre ? "" : " *"}</span>
          <select value={f.mask || ""} onChange={(e) => { const m = GED_MASKS.find((x) => x.id === e.target.value); set({ mask: e.target.value, folder: m ? (m.f === "juridique" && libre ? f.folder : m.f) : f.folder, vis: rename ? f.vis : (m && m.vis) || "partage" }); }}>
            <option value="">{libre ? "— Garder le nom d'origine —" : "— Choisir un masque —"}</option>
            {GED_MASKS.map((m) => <option key={m.id} value={m.id}>{m.l}</option>)}
          </select>
        </label>
        {mask && gedTokens(mask).map((t) => {
          const T = GED_TOKENS[t];
          return (
            <label key={t} className="ged-f"><span>{T.l} *</span>
              <input type={T.type === "year" ? "number" : T.type === "text" ? "text" : T.type} placeholder={T.ph || ""} value={f.vals[t] || ""} onChange={(e) => set({ vals: { ...f.vals, [t]: e.target.value } })} />
            </label>
          );
        })}
        <label className="ged-f"><span>Dossier</span><GedFolderSelect value={f.folder} st={st} allowNew onChange={(v) => set({ folder: v })} /></label>
        {!rename &&
          <div className="ged-f"><span>Visibilité</span>
            <GedSeg value={f.vis} onChange={(v) => set({ vis: v })} options={[["partage", "Partagé avec le client"], ["interne", "Interne cabinet"]]} />
          </div>
        }
      </div>
      {f.folder === "__newjur" && <GedJurFields v={f.newJur || {}} onChange={(v) => set({ newJur: v })} />}
      <div className="ged-frow-res">
        <span className="lab">Nom du fichier</span>
        {/* Nom généré par le masque uniquement (pas de saisie manuelle, à la
            demande du cabinet) ; dans Juridique sans masque, nom d'origine. */}
        {mask ? <GedNamePreview parts={gedParts(mask, f.vals, client)} ext={f.ext} /> : libre ? <GedNamePreview parts={[{ txt: gedNomOrigine(f.orig) }]} ext={f.ext} /> : <span className="ged-muted">Choisir un masque pour générer le nom</span>}
        {f.dup && <span className="ged-tag warn">Nom existant : suffixe ajouté</span>}
        {f.vis === "interne" && <span className="ged-tag dark"><Icon name="lock" size={11} /> Non visible par le client</span>}
      </div>
    </div>
  );
};

const GedMail = ({ client, rec, names }) => (
  <div className="ged-mail">
    <div className="ged-mail-h">
      <div><b>À</b>{rec.name} &lt;{rec.email}&gt;</div>
      <div><b>Objet</b>{names.length > 1 ? `${names.length} nouveaux documents` : "Nouveau document"} dans votre espace documentaire</div>
    </div>
    <div className="ged-mail-b">
      <p>Bonjour,</p>
      <p>Votre cabinet a déposé {names.length > 1 ? `${names.length} nouveaux documents` : "un nouveau document"} dans l'espace documentaire de {client.name} sur MyALFA Link :</p>
      <ul>{names.map((n, i) => <li key={i}>{n}</li>)}</ul>
      <p><span className="ged-mail-btn">Accéder à mon espace documentaire</span></p>
      <p className="sig">{gedMoi()} — {gedCabinet()}</p>
    </div>
  </div>
);

const GedUpload = ({ client, st, onClose, renameDoc, initialFolder }) => {
  const rename = !!renameDoc;
  const docs = st.docs;
  const mk = (orig, extra) => ({ key: Math.random().toString(36).slice(2), orig, ext: gedExt(orig), mask: "", vals: gedDefaults(), folder: initialFolder || "divers", vis: "partage", ...extra });
  const [files, setFiles] = React.useState(() => rename ? [mk(renameDoc.name, { folder: renameDoc.folder, vis: renameDoc.vis })] : []);
  // Notification du client décochée par défaut : le cabinet coche quand il
  // veut prévenir le client (demande du cabinet).
  const [notify, setNotify] = React.useState(false);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const rec0 = React.useMemo(() => gedRecipient(client), [client.name]);
  const [rec, setRec] = React.useState(rec0);
  const [over, setOver] = React.useState(false);
  const inputRef = React.useRef(null);
  // Contrôle avant envoi, mêmes règles que le serveur : 20 Mo par fichier et
  // formats acceptés par MyALFA Link. Un fichier refusé n'est pas ajouté.
  const [refus, setRefus] = React.useState([]);
  const add = (list) => {
    const ok = [];
    const ko = [];
    [...list].forEach((x) => {
      const ext = (/\.[A-Za-z0-9]{2,5}$/.exec(x.name) || [""])[0].toLowerCase();
      if (!GED_EXTENSIONS.includes(ext)) ko.push(`${x.name} (format non accepté)`);
      else if (x.size > GED_TAILLE_MAX) ko.push(`${x.name} (plus de 20 Mo)`);
      else ok.push(x);
    });
    setRefus(ko);
    if (ok.length) setFiles((fs) => [...fs, ...ok.map((x) => mk(x.name, { file: x }))]);
  };
  const [busy, setBusy] = React.useState(false);
  const [err, setErr] = React.useState(null);
  // Avancement de l'envoi (0 à 1) ; -1 = en cours sans mesure (renommage).
  const [progres, setProgres] = React.useState(0);

  const taken = new Set(docs.filter((d) => !rename || d.id !== renameDoc.id).map((d) => d.name.toLowerCase()));
  const resolved = files.map((f) => {
    const mask = GED_MASKS.find((m) => m.id === f.mask);
    const libre = !mask && gedEstJuridique(f.folder);
    const folderOk = f.folder !== "__newjur" || (f.newJur && f.newJur.date && (f.newJur.objet || "").trim());
    if ((!mask && !libre) || !folderOk) return { ...f, final: null };
    const parts = libre ? [{ txt: gedNomOrigine(f.orig) }] : gedParts(mask, f.vals, client);
    if (parts.some((p) => p.missing)) return { ...f, final: null };
    let base = parts.map((p) => p.txt).join(""), n = base + f.ext, i = 2, dup = false;
    while (taken.has(n.toLowerCase())) { n = `${base} (${i++})${f.ext}`; dup = true; }
    taken.add(n.toLowerCase());
    return { ...f, final: n, dup };
  });
  const ready = resolved.length > 0 && resolved.every((f) => f.final);
  const shared = resolved.filter((f) => f.vis === "partage");
  const sharedNames = shared.map((f) => f.final || "(nom à compléter)");
  const canNotify = !rename && shared.length > 0;

  const submit = async () => {
    if (busy) return;
    setBusy(true);
    setErr(null);
    setProgres(rename ? -1 : 0);
    try {
      // Sous-dossiers juridiques à créer d'abord (un par libellé).
      const crees = {};
      const dossierDe = async (f) => {
        if (f.folder !== "__newjur") return f.folder;
        const cle = f.newJur.date + "|" + f.newJur.objet.trim().toLowerCase();
        if (!crees[cle]) {
          const r = await creerSousDossierApi(client.id, { date: f.newJur.date, objet: f.newJur.objet });
          GedStore.sousDossier(client, r.sousDossier);
          crees[cle] = "juridique/" + r.sousDossier.id;
        }
        return crees[cle];
      };
      if (rename) {
        const it = resolved[0];
        const r = await modifierDocumentApi(renameDoc.id, { nom: it.final, dossier: await dossierDe(it) });
        GedStore.documents(client, [r.document]);
        showStatusToast({ status: "success", titre: "Document renommé", message: r.document.name });
      } else {
        // 90 % de la barre pour l'envoi des fichiers (au prorata de leur
        // taille), le reste pour l'enregistrement dans la GED.
        const total = resolved.reduce((s, f) => s + (f.file.size || 1), 0);
        let envoye = 0;
        const documents = [];
        for (const f of resolved) {
          const deja = envoye;
          const blob = await televerser(f.file, (charge) => setProgres((0.9 * (deja + charge)) / total));
          envoye += f.file.size || 1;
          setProgres((0.9 * envoye) / total);
          documents.push({ nom: f.final, url: blob.url, taille: f.file.size, dossier: await dossierDe(f), visibilite: f.vis });
        }
        const r = await deposerApi(client.id, { documents, notifier: canNotify && notify, destinataire: rec.email });
        setProgres(1);
        GedStore.documents(client, r.documents);
        const nb = r.documents.length;
        const n = r.notification;
        showStatusToast({
          status: "success",
          titre: `${nb} document${nb > 1 ? "s déposés" : " déposé"}`,
          message: n && n.envoyee ? `Mail bien envoyé au client (${n.adresse}).` : "Aucun mail envoyé au client.",
        });
        if (n && !n.envoyee) showStatusToast({ status: "error", titre: "Notification non envoyée", message: n.message });
      }
      onClose();
    } catch (e) {
      console.error("GED :", e);
      setErr(e.message || "L'opération a échoué.");
      showStatusToast({ status: "error", titre: rename ? "Renommage impossible" : "Dépôt impossible", message: e.message || "L'opération a échoué." });
      setBusy(false);
      setProgres(0);
    }
  };

  return (
    <div className="ged-scrim" onClick={busy ? undefined : onClose}>
      <div className={`ged-dlg ${busy ? "busy" : ""}`} onClick={(e) => e.stopPropagation()}>
        {busy && (
          <div className="ged-dlg-prog" role="progressbar" aria-valuemin={0} aria-valuemax={100} aria-valuenow={progres >= 0 ? Math.round(progres * 100) : undefined}>
            <i className={progres < 0 ? "indet" : ""} style={progres >= 0 ? { width: `${Math.max(3, Math.round(progres * 100))}%` } : undefined} />
          </div>
        )}
        <div className="ged-dlg-h">
          <span className="ged-dlg-ico"><Icon name={rename ? "pencil" : "download"} size={16} /></span>
          <div><h3>{rename ? "Renommer selon un masque" : "Déposer des documents"}</h3><p>{client.name} · le nom de chaque fichier est généré par le masque choisi (facultatif dans Juridique)</p></div>
          <button className="x-btn" onClick={onClose} disabled={busy}><Icon name="x" size={16} /></button>
        </div>
        <div className="ged-dlg-b">
          {rename && renameDoc.origin === "client" && <div className="ged-note"><Icon name="info" size={13} /> Document déposé par le client le {gedFmt(renameDoc.date)}. Il reste visible par le client sous son nouveau nom.</div>}
          {!rename &&
            <div className={`ged-drop ${over ? "over" : ""} ${files.length ? "compact" : ""}`} onClick={() => inputRef.current.click()}
              onDragOver={(e) => { e.preventDefault(); setOver(true); }} onDragLeave={() => setOver(false)}
              onDrop={(e) => { e.preventDefault(); setOver(false); add(e.dataTransfer.files); }}>
              <input ref={inputRef} type="file" multiple hidden onChange={(e) => { add(e.target.files); e.target.value = ""; }} />
              <Icon name="download" size={files.length ? 15 : 22} />
              <div><b>{files.length ? "Ajouter d'autres fichiers" : "Glisser les fichiers ici, ou cliquer pour parcourir"}</b>{!files.length && <span>Plusieurs fichiers possibles, 20 Mo maximum chacun. Un masque de renommage sera demandé pour chacun, sauf dans Juridique.</span>}</div>
            </div>
          }
          {refus.length > 0 && <div className="ged-note"><Icon name="alert" size={13} /> Fichier refusé : {refus.join(", ")}. Formats acceptés : PDF, Word, Excel, JPEG, PNG, WebP, HEIC.</div>}
          {files.map((f, i) =>
            <GedFileRow key={f.key} f={resolved[i]} client={client} st={st} rename={rename}
              onChange={(nf) => setFiles((fs) => fs.map((x) => x.key === f.key ? { ...nf, final: undefined, dup: undefined } : x))}
              onRemove={rename ? null : () => setFiles((fs) => fs.filter((x) => x.key !== f.key))} />
          )}
          {!rename && files.length > 0 &&
            <div className="ged-notif">
              <div className="ged-notif-h">
                <Icon name="bell" size={14} />
                <span className="t">Notification au client</span>
                {canNotify && <label className="ged-switch"><input type="checkbox" checked={notify} onChange={(e) => setNotify(e.target.checked)} /><span>{notify ? "Notifier le client" : "Ne pas notifier"}</span></label>}
              </div>
              {!canNotify && <p className="ged-muted">Aucun document partagé dans ce dépôt : pas de notification au client.</p>}
              {canNotify && notify && <>
                <div className="ged-rec"><label className="ged-f"><span>Destinataire</span><input value={rec.email} onChange={(e) => setRec({ ...rec, email: e.target.value })} /></label></div>
                <GedMail client={client} rec={rec} names={sharedNames} />
                {st.notification && st.notification.test && <p className="ged-muted">Mode test : le mail part vers {st.notification.adresseTest}, pas vers le client.</p>}
                {resolved.some((f) => f.vis === "interne") && <p className="ged-muted"><Icon name="lock" size={11} /> Les documents internes ne figurent pas dans l'email.</p>}
              </>}
              {canNotify && !notify && <p className="ged-muted">Les documents partagés seront visibles dans l'espace du client, sans email.</p>}
            </div>
          }
        </div>
        <div className="ged-dlg-f">
          {!busy && !rename && files.length === 0 && <span className="ged-muted">Ajoutez au moins un fichier pour pouvoir déposer.</span>}
          {!busy && !ready && files.length > 0 && <span className="ged-muted">Compléter le masque, les champs et le dossier de chaque fichier.</span>}
          {busy && <span className="ged-muted ged-prog-txt">{progres >= 0 ? `Envoi ${Math.round(progres * 100)} %` : "Enregistrement…"}</span>}
          {err && !busy && <span className="ged-tag warn">{err}</span>}
          <button className="btn ghost" onClick={onClose} disabled={busy}>Annuler</button>
          <button className={`btn primary ${busy ? "en-cours" : ""}`} disabled={!ready || busy} onClick={submit}>
            {busy ? (
              <><span className="ged-spin" /> {rename ? "Renommage en cours…" : "Dépôt en cours…"}</>
            ) : (
              <><Icon name="check" size={14} /> {rename ? "Renommer" : `Déposer ${files.length || ""} document${files.length > 1 ? "s" : ""}`}{!rename && canNotify && notify ? " et notifier" : ""}</>
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

// Suppression : confirmée en ressaisissant le nom du client.
const GedSuppression = ({ client, doc, onCancel, onConfirm }) => {
  const [saisie, setSaisie] = React.useState("");
  const ok = gedNorm(saisie) !== "" && gedNorm(saisie) === gedNorm(client.name);
  return (
    <div className="ged-scrim" onClick={onCancel}>
      <div className="ged-dlg sm" onClick={(e) => e.stopPropagation()}>
        <div className="ged-dlg-h">
          <span className="ged-dlg-ico danger"><Icon name="trash" size={16} /></span>
          <div><h3>Supprimer ce document ?</h3><p>Cette action est définitive.</p></div>
          <button className="x-btn" onClick={onCancel}><Icon name="x" size={16} /></button>
        </div>
        <div className="ged-dlg-b">
          <p className="ged-suppr-txt">
            Voulez-vous vraiment supprimer <b>{doc.name}</b> de la GED de <b>{client.name}</b> ?
            {doc.vis === "partage" && " Il disparaîtra aussi de l'espace documentaire du client (MyALFA Link)."}
          </p>
          <label className="ged-f">
            <span>Pour confirmer, saisissez le nom du dossier client : <b className="ged-suppr-nom">{client.name}</b></span>
            <input autoFocus value={saisie} onChange={(e) => setSaisie(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter" && ok) onConfirm(); if (e.key === "Escape") onCancel(); }} placeholder={client.name} />
          </label>
        </div>
        <div className="ged-dlg-f">
          <button className="btn ghost" onClick={onCancel}>Annuler</button>
          <button className="btn danger" disabled={!ok} onClick={onConfirm}><Icon name="trash" size={14} /> Supprimer définitivement</button>
        </div>
      </div>
    </div>
  );
};

// ── Vue 3 colonnes : arborescence · fichiers · aperçu ───────────────
export const GedTab = ({ client }) => {
  const [, force] = React.useReducer((x) => x + 1, 0);
  React.useEffect(() => GedStore.sub(force), []);
  React.useEffect(() => {
    let annule = false;
    chargerGedApi(client.id)
      .then((data) => { if (!annule) GedStore.poser(client, data); })
      .catch((e) => { if (!annule) GedStore.erreur(client, e.message || "Chargement impossible."); });
    return () => { annule = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [client.id]);
  const st = GedStore.get(client);
  const [q, setQ] = React.useState("");
  const [filter, setFilter] = React.useState("all");
  const [showEmpty, setShowEmpty] = React.useState(false);
  const [revealed, setRevealed] = React.useState({});
  const [open, setOpen] = React.useState(() => new Set(["compta", "juridique", "mission"]));
  const [cur, setCur] = React.useState("all");
  const [sel, setSel] = React.useState(null);
  const [dlg, setDlg] = React.useState(null);
  const [newJur, setNewJur] = React.useState(null);
  const [fresh, setFresh] = React.useState(() => new Set());

  const todo = st.docs.filter((d) => !d.ok);
  const s = q.trim();
  const match = (d) => (filter === "all" || (filter === "todo" ? !d.ok : filter === "interne" ? d.vis === "interne" : d.origin === filter)) && (!s || gedCorrespond(d.name, s));
  const inPath = (d, p) => p === "all" || d.folder === p || d.folder.startsWith(p + "/");
  const countIn = (p) => st.docs.filter((d) => match(d) && inPath(d, p)).length;
  const global = !!s || filter !== "all";
  const list = st.docs.filter((d) => match(d) && (global || inPath(d, cur))).sort((x, y) => y.date.localeCompare(x.date));
  const showPath = global || cur === "all" || !cur.includes("/");
  const doc = sel && st.docs.find((d) => d.id === sel);
  const toggle = (p) => setOpen((o) => { const n = new Set(o); n.has(p) ? n.delete(p) : n.add(p); return n; });
  const [actionErr, setActionErr] = React.useState(null);
  const [suppr, setSuppr] = React.useState(null);
  // Changement de visibilité : instantané à l'écran, annulé si le serveur refuse.
  const toggleVis = async (d) => {
    const vis = d.vis === "partage" ? "interne" : "partage";
    GedStore.documents(client, [{ ...d, vis }]);
    try {
      const r = await modifierDocumentApi(d.id, { visibilite: vis });
      GedStore.documents(client, [r.document]);
      showStatusToast({ status: "success", titre: vis === "interne" ? "Document rendu interne" : "Document partagé avec le client", message: d.name });
    } catch (e) {
      GedStore.documents(client, [d]);
      showStatusToast({ status: "error", titre: "Modification impossible", message: e.message || "" });
    }
  };
  // Suppression : le document disparaît tout de suite de la GED, remis en
  // place si le serveur refuse.
  const supprimer = async (d) => {
    setSuppr(null);
    setSel(null);
    GedStore.retirer(client, d.id);
    const toast = showStatusToast({ status: "loading", titre: "Suppression…", message: d.name });
    try {
      await supprimerDocumentApi(d.id);
      toast.update({ status: "success", titre: "Document supprimé", message: d.name });
    } catch (e) {
      GedStore.documents(client, [d]);
      toast.update({ status: "error", titre: "Suppression impossible", message: e.message || "" });
    }
  };
  const createJur = async () => {
    if (!newJur.date || !(newJur.objet || "").trim()) return;
    setActionErr(null);
    try {
      const r = await creerSousDossierApi(client.id, { date: newJur.date, objet: newJur.objet });
      const nid = r.sousDossier.id;
      GedStore.sousDossier(client, r.sousDossier);
      setFresh((f) => new Set([...f, nid])); setOpen((o) => new Set([...o, "juridique"])); setCur("juridique/" + nid); setNewJur(null);
    } catch (e) {
      setActionErr(e.message || "Création impossible.");
    }
  };
  const pick = (p) => { setCur(p); setSel(null); };

  return (
    <>
      <div className="ged-wrap">
        <div className="ged-bar">
          <div className="ged-search"><Icon name="search" size={14} /><input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Rechercher dans toute la GED…" /></div>
          <select className="ged-filter" value={filter} onChange={(e) => setFilter(e.target.value)}>
            <option value="all">Tous les documents</option>
            <option value="cabinet">Déposés par le cabinet</option>
            <option value="client">Déposés par le client</option>
            <option value="interne">Internes cabinet</option>
            <option value="todo">À renommer</option>
          </select>
          <button className="btn primary" onClick={() => setDlg({ folder: cur !== "all" ? cur : undefined })}><Icon name="plus" size={14} /> Déposer</button>
        </div>
        {actionErr && <div className="ged-todo"><Icon name="alert" size={14} /><span>{actionErr}</span></div>}
        {todo.length > 0 &&
          <div className="ged-todo">
            <Icon name="alert" size={14} />
            <span><b>{todo.length} document{todo.length > 1 ? "s" : ""} déposé{todo.length > 1 ? "s" : ""} par le client</b> sans nom normalisé, à renommer selon un masque.</span>
            <button className="linkbtn" onClick={() => setFilter(filter === "todo" ? "all" : "todo")}>{filter === "todo" ? "Tout afficher" : "Afficher"}</button>
          </div>
        }
        <div className={`ged-cols ${doc ? "has-view" : ""}`}>
          {/* 1 — Arborescence */}
          <nav className="ged-tree">
            <button className={`ged-tn root ${cur === "all" ? "on" : ""}`} style={{ "--d": 0 }} onClick={() => pick("all")}>
              <span className="ged-chev"></span><Icon name="archive" size={14} /><span className="n">Tous les documents</span>{st.chargement ? <i className="ged-skel" /> : <i>{countIn("all")}</i>}
            </button>
            {GED_TREE.map((r) => {
              const subs = gedSubs(r, st);
              const visSubs = subs.filter((x) => showEmpty || revealed[r.id] || fresh.has(x.id) || cur === r.id + "/" + x.id || countIn(r.id + "/" + x.id) > 0);
              const hidden = subs.length - visSubs.length;
              const op = open.has(r.id);
              return (
                <div key={r.id} className="ged-tb">
                  <div className={`ged-tn root ${cur === r.id ? "on" : ""}`} style={{ "--d": 0 }} onClick={() => pick(r.id)}>
                    <span className="ged-chev" onClick={(e) => { e.stopPropagation(); toggle(r.id); }}>{(subs.length > 0 || r.dyn) && <Icon name="chev" size={11} style={{ transform: op ? "rotate(90deg)" : "none" }} />}</span>
                    <Icon name="folder" size={14} /><span className="n">{r.l}</span>{st.chargement ? <i className="ged-skel" /> : <i>{countIn(r.id) || ""}</i>}
                    {r.dyn && <button className="ged-mini" title="Nouveau sous-dossier" onClick={(e) => { e.stopPropagation(); setNewJur({ date: gedISO(gedToday()), objet: "" }); setOpen((o) => new Set([...o, r.id])); }}><Icon name="plus" size={11} /></button>}
                  </div>
                  {op && <>
                    {r.dyn && st.chargement && [70, 55].map((w, i) => (
                      <div key={i} className="ged-tn ged-tn-skel" style={{ "--d": 1 }} aria-hidden="true"><span className="ged-chev"></span><span className="ged-skel" style={{ height: 11, width: w + "%" }} /></div>
                    ))}
                    {r.dyn && newJur &&
                      <div className="ged-newjur">
                        <input type="date" value={newJur.date} onChange={(e) => setNewJur({ ...newJur, date: e.target.value })} />
                        <input autoFocus value={newJur.objet} placeholder="Intitulé" onChange={(e) => setNewJur({ ...newJur, objet: e.target.value })} onKeyDown={(e) => { if (e.key === "Enter") createJur(); if (e.key === "Escape") setNewJur(null); }} />
                        <div><button className="btn sm primary" disabled={!newJur.date || !newJur.objet.trim()} onClick={createJur}>Créer</button><button className="btn sm ghost" onClick={() => setNewJur(null)}>Annuler</button></div>
                      </div>
                    }
                    {visSubs.map((x) => {
                      const p = r.id + "/" + x.id, n = countIn(p);
                      return <button key={x.id} className={`ged-tn ${cur === p ? "on" : ""} ${n ? "" : "empty"}`} style={{ "--d": 1 }} onClick={() => pick(p)}><span className="ged-chev"></span><Icon name="folder" size={13} /><span className="n" title={x.l}>{x.l}</span><i>{n || ""}</i></button>;
                    })}
                    {hidden > 0 && <button className="ged-reveal" onClick={() => setRevealed((v) => ({ ...v, [r.id]: true }))}>+ {hidden} vide{hidden > 1 ? "s" : ""} masqué{hidden > 1 ? "s" : ""}</button>}
                    {revealed[r.id] && !showEmpty && subs.some((x) => !countIn(r.id + "/" + x.id)) && <button className="ged-reveal" onClick={() => setRevealed((v) => ({ ...v, [r.id]: false }))}>Masquer les vides</button>}
                  </>}
                </div>
              );
            })}
            <label className="ged-switch sm ged-tree-f"><input type="checkbox" checked={showEmpty} onChange={(e) => setShowEmpty(e.target.checked)} /><span>Arborescence complète</span></label>
          </nav>

          {/* 2 — Fichiers */}
          <section className="ged-files">
            <div className="ged-files-h">
              <div className="t">{global ? "Résultats" : cur === "all" ? "Tous les documents" : gedPathLabel(cur, st)}{st.chargement ? <i className="ged-skel ged-skel-txt" /> : <i>{list.length} document{list.length > 1 ? "s" : ""}</i>}</div>
              {!global && cur !== "all" && <button className="btn sm" onClick={() => setDlg({ folder: cur })}><Icon name="plus" size={12} /> Déposer ici</button>}
            </div>
            <div className="ged-files-l">
              {st.chargement && [0, 1, 2, 3, 4, 5].map((i) => (
                <div key={i} className="ged-file skel" aria-hidden="true">
                  <span className="ged-file-ico ged-skel" />
                  <span className="ged-file-m">
                    <span className="ged-skel" style={{ height: 12, width: `${[62, 48, 70, 55, 66, 40][i]}%` }} />
                    <span className="ged-skel" style={{ height: 10, width: `${[34, 28, 40, 30, 36, 25][i]}%` }} />
                  </span>
                </div>
              ))}
              {st.erreur && <div className="ged-empty"><Icon name="alert" size={20} /><div>Chargement impossible : {st.erreur}</div></div>}
              {!st.chargement && !st.erreur && list.length === 0 && <div className="ged-empty"><Icon name="folder" size={20} /><div>Aucun document dans ce dossier.</div></div>}
              {list.map((d) =>
                <button key={d.id} className={`ged-file ${sel === d.id ? "on" : ""} ${d.ok ? "" : "todo"}`} onClick={() => setSel(sel === d.id ? null : d.id)}>
                  <span className="ged-file-ico"><Icon name="fileText" size={16} /><em>{gedExt(d.name).slice(1).toUpperCase()}</em></span>
                  <span className="ged-file-m">
                    <span className="n" title={d.name}>{d.name}</span>
                    <span className="s">
                      <span className={`ged-orig ${d.origin}`}>{d.origin === "cabinet" ? "Cabinet" : "Client"}</span>
                      <span>{d.by}</span><span>·</span><span>{gedFmt(d.date)}</span>
                      {showPath && <><span>·</span><span className="p">{gedPathLabel(d.folder, st)}</span></>}
                    </span>
                  </span>
                  <span className="ged-file-st">
                    {!d.ok && <span className="ged-tag warn">À renommer</span>}
                    {d.vis === "interne" ? <span className="ged-ic dark" title="Interne cabinet"><Icon name="lock" size={11} /></span>
                      : d.origin === "cabinet" && <span className={`ged-ic ${d.notified ? "ok" : ""}`} title={d.notified ? `Client notifié le ${gedFmt(d.notified)}` : "Client non notifié"}><Icon name="bell" size={11} /></span>}
                  </span>
                </button>
              )}
            </div>
          </section>

          {/* 3 — Aperçu */}
          {doc &&
            <aside className="ged-view">
              <div className="ged-view-h">
                <span className="t" title={doc.name}>{doc.name}</span>
                <button className="x-btn ged-x" onClick={() => setSel(null)} title="Fermer l'aperçu"><Icon name="x" size={13} /></button>
              </div>
              <div className="ged-view-p">
                {doc.url && gedExt(doc.name) === ".pdf" ? (
                  <iframe className="ged-view-doc" src={doc.url} title={doc.name}></iframe>
                ) : doc.url && [".png", ".jpg", ".jpeg", ".webp"].includes(gedExt(doc.name)) ? (
                  <img className="ged-view-doc" src={doc.url} alt={doc.name} />
                ) : (
                <div className="ged-page">
                  <div className="ph"><span className="lg">{gedCode(client).slice(0, 2)}</span><span>{client.name}</span></div>
                  <div className="tt">{([...GED_MASKS].sort((a, b) => b.l.length - a.l.length).find((m) => doc.name.startsWith(m.l)) || {}).l || "Document"}</div>
                  {[90, 75, 82, 60, 88, 70, 0, 85, 78, 66, 80, 40].map((w, i) => <div key={i} className="ln" style={{ width: w + "%" }}></div>)}
                  <span className="pg">1 / {1 + (doc.id % 4)}</span>
                </div>
                )}
                <span className="ged-view-note">Aperçu du document</span>
              </div>
              <dl className="ged-view-i">
                <dt>Dossier</dt><dd>{gedPathLabel(doc.folder, st)}</dd>
                <dt>Origine</dt><dd><span className={`ged-orig ${doc.origin}`}>{doc.origin === "cabinet" ? "Cabinet" : "Client"}</span> {doc.by}</dd>
                <dt>Déposé le</dt><dd>{gedFmt(doc.date)}</dd>
                <dt>Visibilité</dt><dd>{doc.vis === "interne" ? <span className="ged-tag dark"><Icon name="lock" size={11} /> Interne cabinet</span> : <span className="ged-tag share"><Icon name="users" size={11} /> Partagé avec le client</span>}</dd>
                {doc.origin === "cabinet" && doc.vis === "partage" && <><dt>Notification</dt><dd>{doc.notified ? `Client notifié le ${gedFmt(doc.notified)}` : "Non notifié"}</dd></>}
                <dt>Nom</dt><dd>{doc.ok ? "Conforme au masque" : <span className="ged-tag warn">À renommer selon un masque</span>}</dd>
                {doc.renamed && <><dt>Renommé</dt><dd>le {gedFmt(doc.renamed.date)} par {doc.renamed.by}<br /><span className="ged-muted">ancien nom : {doc.renamed.from}</span></dd></>}
              </dl>
              <div className="ged-view-a">
                <button className="btn sm" disabled={!doc.url} onClick={() => doc.url && window.open(doc.url, "_blank", "noopener")}><Icon name="download" size={13} /> Télécharger</button>
                <button className={`btn sm ${doc.ok ? "" : "primary"}`} onClick={() => setDlg({ rename: doc })}><Icon name="pencil" size={13} /> Renommer</button>
                {doc.origin === "cabinet" && <button className="btn sm" onClick={() => toggleVis(doc)}><Icon name={doc.vis === "partage" ? "lock" : "users"} size={13} /> {doc.vis === "partage" ? "Rendre interne" : "Partager"}</button>}
                <button className="btn sm danger" onClick={() => setSuppr(doc)}><Icon name="trash" size={13} /> Supprimer</button>
              </div>
            </aside>
          }
        </div>
      </div>
      {dlg && createPortal(<div className="gedx"><GedUpload client={client} st={st} renameDoc={dlg.rename} initialFolder={dlg.folder} onClose={() => setDlg(null)} /></div>, document.body)}
      {suppr && createPortal(<div className="gedx"><GedSuppression client={client} doc={suppr} onCancel={() => setSuppr(null)} onConfirm={() => supprimer(suppr)} /></div>, document.body)}
    </>
  );
};
